import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { AppShell } from "@/components/app/AppShell";

const STATUSES = { new: "New", replied: "Replied", archived: "Archived" };
const selectCls = "h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-oath-ink";

const StatusSelect = ({ row, onDone }) => {
  const change = async (e) => {
    try {
      await api.post(`/admin/contact-messages/${row.id}/status`, { status: e.target.value });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <select value={row.status} onChange={change} className={selectCls} data-testid={`message-status-${row.id}`}>
      {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );
};

export default function AdminMessages() {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/contact-messages?status=${status}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <AppShell eyebrow="— SUPER ADMIN" title="Contact messages">
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm" data-testid="messages-status-filter">
        <option value="">All statuses</option>
        {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <div className="mt-5 overflow-x-auto rounded-2xl border border-gray-200 bg-white">
        <table className="w-full min-w-[860px] text-left text-sm" data-testid="messages-table">
          <thead className="border-b border-gray-200 bg-oath-bg/60 font-mono-label text-[9px] text-oath-muted">
            <tr><th className="px-4 py-3">FROM</th><th className="px-4 py-3">TOPIC</th><th className="px-4 py-3">MESSAGE</th><th className="px-4 py-3">RECEIVED</th><th className="px-4 py-3">STATUS</th></tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={5} className="px-4 py-8"><Loader2 className="h-5 w-5 animate-spin text-oath-muted" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-oath-muted" data-testid="messages-empty">No messages yet.</td></tr>}
            {rows?.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 align-top last:border-0" data-testid={`message-row-${r.id}`}>
                <td className="px-4 py-3 text-oath-secondary">
                  <div className="font-medium text-oath-ink">{r.full_name}</div>
                  <a href={`mailto:${r.email}`} className="text-oath-accent hover:underline">{r.email}</a>
                  {r.company && <div className="text-xs">{r.company}</div>}
                </td>
                <td className="px-4 py-3"><span className="rounded-full bg-oath-accent-light px-2 py-0.5 text-[11px] text-oath-accent">{r.topic}</span></td>
                <td className="max-w-md whitespace-pre-wrap px-4 py-3 text-xs text-oath-secondary">{r.message}</td>
                <td className="px-4 py-3 font-mono text-xs text-oath-secondary">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3"><StatusSelect row={r} onDone={load} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
